/**
 * Route Metadata Configuration
 * Declarative route protection rules for the application
 */

import type { PermissionKey } from '../../../types/rbac/catalogue'

export interface RouteMetadata {
  // Route path
  path: string
  
  // Required permissions (array of permission strings; the user needs at least one).
  // Entries are either the legacy flags in PERMISSIONS or a company permission key from the
  // catalogue (types/rbac/catalogue.ts), which is checked against GET /api/rbac/me.
  // Prefix with '!' for negation (e.g., '!company_access' means user must NOT have company access)
  requires?: string[]
  
  // Is this a public route (no authentication required)
  public?: boolean
  
  // Redirect target if access is denied
  redirectTo?: string
  
  // Route category/group
  category?: 'public' | 'admin' | 'company' | 'user'
  
  // Additional metadata
  metadata?: Record<string, any>
}

// Permission strings used in route metadata
export const PERMISSIONS = {
  GLOBAL_ADMIN: 'global_admin',
  PLATFORM_ADMIN: 'platform_admin',
  COMPANY_ACCESS: 'company_access',
  NO_COMPANY_ACCESS: '!company_access', // Negation - user must NOT have company access
  FULL_ACCESS: 'full_access',
  BILLING_ACCESS: 'billing_access',
} as const

/**
 * A /company page that needs a catalogue key (SC-439). Platform admins pass as before; everyone
 * else needs one of the keys, so a member without the key goes back to the overview, which every
 * member may open.
 */
function companyRouteRequiring(path: string, keys: PermissionKey[]): RouteMetadata {
  return {
    path,
    requires: [...keys, PERMISSIONS.PLATFORM_ADMIN, PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/company/overview',
    category: 'company',
  }
}

// Route metadata definitions
export const ROUTE_METADATA: Record<string, RouteMetadata> = {
  // Public routes
  '/': {
    path: '/',
    public: true,
    category: 'public',
  },
  '/dashboard': {
    path: '/dashboard',
    public: true,
    category: 'public',
  },
  '/publicLobby': {
    path: '/publicLobby',
    public: true,
    category: 'public',
  },
  '/join': {
    path: '/join',
    public: true,
    category: 'public',
  },
  '/accept-invitation': {
    path: '/accept-invitation',
    public: true,
    category: 'public',
  },
  '/join-company': {
    path: '/join-company',
    public: true,
    category: 'public',
  },
  '/invitation-email-preview': {
    path: '/invitation-email-preview',
    public: true,
    category: 'public',
  },
  '/room': {
    path: '/room',
    public: true,
    category: 'public',
  },
  '/rooms': {
    path: '/rooms',
    public: true,
    category: 'user',
  },
  '/myRooms': {
    path: '/myRooms',
    public: true,
    category: 'user',
  },
  '/meetings': {
    path: '/meetings',
    public: true,
    category: 'user',
  },
  // SC-473: the dashboard Chat page. Not gated by chat.use, like the in-room chat (menuAccess.ts,
  // user decision 2026-10-24). Guests are not blocked by the router: the page and the sidebar
  // item hide themselves for a guest, which is what the in-room chat item does.
  '/chat': {
    path: '/chat',
    public: true,
    category: 'user',
  },
  '/profile': {
    path: '/profile',
    public: true,
    category: 'user',
  },
  '/profile/edit': {
    path: '/profile/edit',
    public: true,
    category: 'user',
  },
  '/settings': {
    path: '/settings',
    public: true,
    category: 'user',
  },
  
  // Admin routes - Global Admin only
  '/admin': {
    path: '/admin',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminMenu': {
    path: '/adminMenu',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/dashboard': {
    path: '/admin/dashboard',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminDashboard': {
    path: '/adminDashboard',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/environment': {
    path: '/admin/environment',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminEnvironmentConfig': {
    path: '/adminEnvironmentConfig',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/entities': {
    path: '/admin/entities',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminEntityCatalogue': {
    path: '/adminEntityCatalogue',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/rooms': {
    path: '/admin/rooms',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminDefaultRooms': {
    path: '/adminDefaultRooms',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminAllRooms': {
    path: '/adminAllRooms',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/billing': {
    path: '/admin/billing',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminBillingSettings': {
    path: '/adminBillingSettings',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/server-jobs': {
    path: '/admin/server-jobs',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminServerJobs': {
    path: '/adminServerJobs',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/maps': {
    path: '/admin/maps',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminMaps': {
    path: '/adminMaps',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/transactional-emails': {
    path: '/admin/transactional-emails',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminTransactionalEmails': {
    path: '/adminTransactionalEmails',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/room-management': {
    path: '/admin/room-management',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminRoomManagement': {
    path: '/adminRoomManagement',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/users': {
    path: '/admin/users',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminUserManagement': {
    path: '/adminUserManagement',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/endpoint-testing': {
    path: '/admin/endpoint-testing',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminEndpointTesting': {
    path: '/adminEndpointTesting',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/product-updates': {
    path: '/admin/product-updates',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminProductUpdates': {
    path: '/adminProductUpdates',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/map-editor-plugins': {
    path: '/admin/map-editor-plugins',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/adminMapEditorPlugins': {
    path: '/adminMapEditorPlugins',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/admin/routing-test': {
    path: '/admin/routing-test',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  '/routingTest': {
    path: '/routingTest',
    requires: [PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'admin',
  },
  
  // Company routes
  '/company': {
    path: '/company',
    requires: [PERMISSIONS.COMPANY_ACCESS, PERMISSIONS.PLATFORM_ADMIN, PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/company/overview',
    category: 'company',
  },
  '/companyManagement': {
    path: '/companyManagement',
    requires: [PERMISSIONS.COMPANY_ACCESS, PERMISSIONS.PLATFORM_ADMIN, PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/company/overview',
    category: 'company',
  },
  '/company/overview': {
    path: '/company/overview',
    requires: [PERMISSIONS.COMPANY_ACCESS, PERMISSIONS.PLATFORM_ADMIN, PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'company',
  },
  '/company/dashboard': {
    path: '/company/dashboard',
    requires: [PERMISSIONS.COMPANY_ACCESS, PERMISSIONS.PLATFORM_ADMIN, PERMISSIONS.GLOBAL_ADMIN],
    redirectTo: '/dashboard',
    category: 'company',
  },
  '/company/billing': companyRouteRequiring('/company/billing', ['billing.view_plan']),
  '/company/users': companyRouteRequiring('/company/users', ['members.view']),
  '/company/settings': companyRouteRequiring('/company/settings', ['workspace.settings.manage']),
  '/company/wallet': companyRouteRequiring('/company/wallet', ['wallet.manage']),
  
  // Special case: Company setup - only for users WITHOUT company access
  '/company/setup': {
    path: '/company/setup',
    requires: [PERMISSIONS.NO_COMPANY_ACCESS, PERMISSIONS.GLOBAL_ADMIN], // Global admins can always access
    redirectTo: '/company/overview',
    category: 'company',
    metadata: {
      allowGlobalAdmin: true, // Global admins can access even with company access
    },
  },
  
  // Company dialogs
  '/company/dialogs/company': companyRouteRequiring(
    '/company/dialogs/company',
    ['workspace.settings.manage']
  ),
  '/company/dialogs/invite-user': companyRouteRequiring(
    '/company/dialogs/invite-user',
    ['members.invite']
  ),
  '/company/dialogs/edit-user': companyRouteRequiring(
    '/company/dialogs/edit-user',
    ['members.edit', 'roles.assign']
  ),
  '/company/dialogs/users': companyRouteRequiring('/company/dialogs/users', ['members.view']),
  '/company/dialogs/company-rooms': companyRouteRequiring(
    '/company/dialogs/company-rooms',
    ['rooms.manage']
  ),
}

const SORTED_ROUTE_METADATA = Object.values(ROUTE_METADATA).sort(
  (a, b) => b.path.length - a.path.length
)

/**
 * Normalize path for consistent matching
 */
function normalizePath(path: string): string {
  if (!path || typeof path !== 'string') {
    return '/'
  }
  const trimmed = path.trim()
  if (trimmed === '' || trimmed === '/') {
    return '/'
  }
  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return normalized.replace(/\/+$/, '')
}

/**
 * Helper function to get route metadata
 * Implements secure-by-default for admin routes
 */
export function getRouteMetadata(path: string): RouteMetadata | null {
  const normalizedPath = normalizePath(path)
  
  // Exact match first (most common case, fastest)
  if (ROUTE_METADATA[normalizedPath]) {
    return ROUTE_METADATA[normalizedPath]
  }
  
  // Security: Explicit check for admin routes - any path starting with /admin requires GLOBAL_ADMIN
  // This ensures all admin routes are protected, even if not explicitly defined
  if (normalizedPath.startsWith('/admin') || normalizedPath === '/admin') {
    // Return a metadata object that requires GLOBAL_ADMIN for any admin route
    return {
      path: normalizedPath,
      requires: [PERMISSIONS.GLOBAL_ADMIN],
      redirectTo: '/dashboard',
      category: 'admin',
    }
  }
  
  // Check for prefix matches (for nested routes)
  // Sort by path length (longest first) to match most specific routes first
  const matchingRoute = SORTED_ROUTE_METADATA.find((metadata) => {
    const metadataPath = normalizePath(metadata.path)
    // Match if the path starts with the metadata path followed by a slash
    // This handles nested routes like /company/overview matching /company
    if (normalizedPath.startsWith(metadataPath + '/')) {
      return true
    }
    // Also handle exact match (shouldn't happen due to first check, but safety)
    if (normalizedPath === metadataPath) {
      return true
    }
    return false
  })
  
  return matchingRoute || null
}

// Helper function to check if route is public
export function isPublicRoute(path: string): boolean {
  const metadata = getRouteMetadata(path)
  return metadata?.public === true
}

// Helper function to get redirect target for denied access
export function getRedirectTarget(path: string): string {
  const metadata = getRouteMetadata(path)
  return metadata?.redirectTo || '/dashboard'
}
